import { useEffect, useRef, useState } from 'react';

import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { mergeRefs } from '@shared/utils/hooks/setRef';

import { useVimeoPlayerContext } from './VimeoPlayerProvider';
import type { VimeoPlayerEventHandlers } from './analytics';
import { VimeoPlayerAnalytics } from './analytics';

type UseVimeoPlayerAnalyticsArgs = {
  handlers?: VimeoPlayerEventHandlers;
  iframeRef?: React.Ref<HTMLIFrameElement>;
};
export const useVimeoPlayerAnalytics = (args: UseVimeoPlayerAnalyticsArgs) => {
  const { handlers, iframeRef } = args;
  const { VimeoPlayer, prepareForUse } = useVimeoPlayerContext();

  const analyticsInstanceRef = useRef<VimeoPlayerAnalytics | null>(null);
  const [iframeNode, setIframeNode] = useState<HTMLIFrameElement | null>(null);

  const vimeoIframeRef = useEventCallback(
    mergeRefs(iframe => {
      setIframeNode(iframe);
    }, iframeRef),
  );

  useEffect(() => {
    // Load the vimeo player library
    prepareForUse();
  }, [prepareForUse]);

  useEffect(() => {
    // Ensure that the event callbacks are always up to date
    analyticsInstanceRef.current?.setEventCallbacks(handlers);
  }, [handlers]);

  useEffect(() => {
    if (VimeoPlayer && iframeNode) {
      const player = new VimeoPlayer(iframeNode);
      const vimeoAnalytics = new VimeoPlayerAnalytics(player, handlers);

      analyticsInstanceRef.current = vimeoAnalytics;
      vimeoAnalytics.initialize();
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VimeoPlayer, iframeNode]);

  return {
    vimeoIframeRef,
  };
};
